import { YaasPositionStatus } from "@solomon-apps/shared";
import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";

import type { Holding } from "../../Positions/holdings";

/** Sentinel selection value for the "All Positions" row. */
export const ALL_POSITIONS = "all";

interface PositionFilterListProps {
    /** One row per holding (Programs abstracted away). */
    holdings: Holding[];
    /** Selected holding's primary uuid, or `ALL_POSITIONS` for the portfolio total. */
    selected: string;
    /** Called with a holding's primary uuid or `ALL_POSITIONS`. */
    onSelect: (value: string) => void;
}

const STATUS_ORDER: Record<YaasPositionStatus, number> = {
    [YaasPositionStatus.Active]: 0,
    [YaasPositionStatus.Pending]: 1,
    [YaasPositionStatus.Rejected]: 2,
    [YaasPositionStatus.Suspended]: 3,
    [YaasPositionStatus.Disabled]: 4,
    [YaasPositionStatus.Hidden]: 5
};

/**
 * YaaS position status → IndicationUnit `status` state. Mirrors the
 * `STATUS_STATE` map in PositionsPage.
 */
const STATUS_STATE: Record<
    YaasPositionStatus,
    "success" | "pending" | "error" | "disabled"
> = {
    [YaasPositionStatus.Active]: "success",
    [YaasPositionStatus.Pending]: "pending",
    [YaasPositionStatus.Rejected]: "error",
    [YaasPositionStatus.Suspended]: "pending",
    [YaasPositionStatus.Disabled]: "disabled",
    [YaasPositionStatus.Hidden]: "disabled"
};

/**
 * Left-rail position filter for the Portfolio page.
 *
 * A compact single-select list — an "All Positions" row (selected by default,
 * showing the portfolio total) followed by one row per holding. Each row shows
 * only the Surface + Position name (mirroring the PositionsPage table naming)
 * and a leading status dot. Selecting a row filters the earnings chart; it does
 * NOT open an editor. Selection is owned by the parent (driven by `?position`).
 */
export function PositionFilterList({
    holdings,
    selected,
    onSelect
}: PositionFilterListProps): React.ReactElement {
    const visible = holdings
        .slice()
        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);

    return (
        <section aria-label="Filter by position">
            <div className="px-4 py-5 border-b border-line-base">
                <Heading title="Filter by Position" titleVariant="h2" />
            </div>

            <List selectable="single" selected={selected} onSelect={onSelect}>
                <ListItem
                    itemKey={ALL_POSITIONS}
                    padding="px-4 py-4"
                    gap="gap-3"
                >
                    <Text
                        variant="body"
                        render={<span />}
                        className="text-foreground-base"
                    >
                        All Positions
                    </Text>
                </ListItem>

                {visible.map((h) => {
                    const isMuted = h.status !== YaasPositionStatus.Active;
                    return (
                        <ListItem
                            key={h.key}
                            itemKey={h.primaryUuid}
                            padding="px-4 py-4"
                            gap="gap-0"
                            className={isMuted ? "opacity-50" : ""}
                        >
                            <div className="min-w-0 flex-1">
                                {/* Dot inline with the title; surface on a
                                    second line indented past the dot —
                                    mirrors the Programs rail's ProgramRow. */}
                                <div className="flex items-center gap-2">
                                    <IndicationUnit
                                        variant="status"
                                        state={STATUS_STATE[h.status]}
                                        size={7}
                                    />
                                    <Text
                                        variant="body"
                                        render={<span />}
                                        className="text-foreground-base block truncate min-w-0"
                                    >
                                        {h.name}
                                    </Text>
                                </div>
                                <Text
                                    variant="label"
                                    className="text-foreground-muted mt-1 pl-4 block truncate min-w-0"
                                >
                                    {h.surface}
                                </Text>
                            </div>
                        </ListItem>
                    );
                })}
            </List>
        </section>
    );
}
